import { SMS_CONSENT_TEXT, SMS_DECLINE_TEXT } from '@aslgo/shared';
import { ConsentText } from '../components/ConsentText.js';

/** The guide's answer to the SMS consent question. A single value rather than
 *  two booleans: the two options are mutually exclusive by construction, so no
 *  contradictory answer can be recorded. `undefined` = unanswered, which is
 *  what both options render as on first paint. */
export type SmsConsentChoice = 'opt_in' | 'opt_out';

/** Twilio / carrier A2P consent capture, shown at registration once the guide
 *  has entered a mobile number. Neither option is preselected and declining never
 *  blocks the application — SMS consent is optional by law and by design. */
export function SmsConsent({ value, onChange, error }: {
  value?: SmsConsentChoice;
  onChange: (choice: SmsConsentChoice) => void;
  error?: string;
}) {
  return (
    <div role="radiogroup" aria-label="Text message notifications" className="flex flex-col gap-3">
      <Option text={SMS_CONSENT_TEXT} checked={value === 'opt_in'} onSelect={() => onChange('opt_in')} />
      <Option text={SMS_DECLINE_TEXT} checked={value === 'opt_out'} onSelect={() => onChange('opt_out')} />
      {error ? <span className="text-[0.8rem] text-status-error dark:text-red-400">{error}</span> : null}
    </div>
  );
}

function Option({ text, checked, onSelect }: { text: string; checked: boolean; onSelect: () => void }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer">
      <input
        type="radio"
        name="sms-consent"
        className="mt-1 h-4 w-4 shrink-0 accent-brand"
        checked={checked}
        onChange={onSelect}
      />
      <span className="text-[0.8rem] leading-relaxed text-ink-600 dark:text-ink-300">
        <ConsentText text={text} />
      </span>
    </label>
  );
}
